import React from 'react';
import { observer } from 'mobx-react';
import { config } from '../../config';

// Helpers
import { closeDrawer, convertMinutesToTimeString, checkValidity, toTop, getURLSegment } from '../../helpers/helpers.js';

// Translations
import translate from '../Translate.js';

// Dependencies
import Slider from 'rc-slider';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Checkbox, CheckboxGroup } from 'react-checkbox-group';
import { RadioGroup, Radio } from 'react-radio-group';
import Textarea from 'react-textarea-autosize';
import { FeatureFlag } from "react-launch-darkly";
import rbIcon from "../../../../public/static/img/icn-registerblast.png"

// Components
import * as icons from '../Icons/Icons';
import Map from '../Map/Map';

// Slider tooltips
const createSliderWithTooltip = Slider.createSliderWithTooltip;
const Range = createSliderWithTooltip(Slider.Range);

const Location = observer(class Location extends React.Component {
  constructor() {
    super();

    // Default state that we will manipulate and send to the API
    this.state = {
      priceGroups: [
        {
          title: 'Global',
          registerBlastExamGroup: null,
          registerBlastWrittenExamGroupKey: null,
          registerBlastHybridExamGroupKey: null,
          smarterSchedulingCalendarSid: null,
          enabled: true,
          priceModel: 'tiered',
          currencies: ['USD'],
          editingCurrencies: false,
          sessionLength: [0, 480],
          account: {
            sid: null,
            name: null
          },
          fees: [
            {
              minimumSessionLength: 0,
              maximumSessionLength: 480,
              amounts: {
                USD: {
                  service: 0,
                  late: 0
                }
              }
            }
          ]
        }
      ],
      timeZoneSid: null,
      currencyCode: null,
      tabs: ['Location', 'Details', 'Pricing'],
      tabsDisabled: false,
      activeTab: 1,
      create: true,
      newLocation: false
    };

    // Bind this to functions
    this.handlePriceOpts = this.handlePriceOpts.bind(this);
    this.setLocation = this.setLocation.bind(this);
    this.updateLocationOpts = this.updateLocationOpts.bind(this);
    this.updateCurrencies = this.updateCurrencies.bind(this);
    this.editCurrencies = this.editCurrencies.bind(this);
    this.addPriceGroup = this.addPriceGroup.bind(this);
    this.onRangeChange = this.onRangeChange.bind(this);
    this.addRange = this.addRange.bind(this);
    this.updateFee = this.updateFee.bind(this);
    this.handleTabs = this.handleTabs.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleLocationSubmit = this.handleLocationSubmit.bind(this);
    this.createLocation = this.createLocation.bind(this);
    this.updateLocation = this.updateLocation.bind(this);
    this.createLocationAvailability = this.createLocationAvailability.bind(this);
    this.handleFeesSubmit = this.handleFeesSubmit.bind(this);
    this.renderPolicies = this.renderPolicies.bind(this);
  }

  componentDidMount() {
    const { LocationStore, ScheduleStore, SessionStore, PolicyStore } = this.props.store;

    // Get API data for time zones, currencies, and location types
    SessionStore.getTimezones();
    SessionStore.getCurrencies();
    SessionStore.getAccounts();
    LocationStore.getLocationTypes();

    // Make sure data from previous locations has been removed
    LocationStore.resetLocation();

    // Get cancellation policies
    PolicyStore.getCancellationPolicies();

    // Check and clear location calendars array
    if (
      ScheduleStore.locationCalendars && ScheduleStore.locationCalendars.length && ScheduleStore.locationCalendars.length > 0
    ) {
      ScheduleStore.locationCalendars = []
    }

    // If this is the register location form, or the add a new location form,
    // set newLocation to true since we’re creating a new location
    if(this.props.routing.location.pathname.indexOf('register') > -1 || this.props.routing.location.pathname.indexOf('add') > -1) {
      this.setState({
        newLocation: true
      });
    }

    // If this is a pre-existing location, get the data
    if (this.props.locationSid) {
      LocationStore.getLocation(this.props.locationSid);
      LocationStore.getLocationAvailability(this);

      if (LocationStore.locations && LocationStore.locations.length && LocationStore.locations.length > 0) {
        // Find corresponding location based on locationSid
        const [currentLocationData] = JSON.parse(JSON.stringify(LocationStore.locations)).filter(x => x.sid === this.props.locationSid)
  
        // If the location scheduling module is 'smarter-scheduling', get location calendars
        if (
          currentLocationData && currentLocationData.schedulingModule === 'smarter-scheduling'
        ) {
          ScheduleStore.getLocationCalendars(this.props.locationSid, true)
        }
      }

      // Set create to false since we’re updating
      this.setState({
        create: false
      })
    } else {
      // Make sure there isn’t an old locationSid in the LocationStore
      this.props.store.LocationStore.locationSid = null;
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const { LocationStore, ScheduleStore, SessionStore } = this.props.store;

    if(
      nextState.activeTab === 3 &&
      SessionStore.account &&
      !LocationStore.gettingGroups &&
      LocationStore.examGroups.length === 0
    ) {
      // Check if there are exam groups
      if(
          (SessionStore.account.metadata && SessionStore.account.metadata.campusKey) ||
          LocationStore.locationData.registerBlastCampusKey
        ) {
        LocationStore.getExamGroups(LocationStore.locationSid);
      }
    }

    // Get location calendars
    if(
      SessionStore.smarterscheduling &&
      LocationStore.locationData.schedulingModule === 'smarter-scheduling' &&
      ScheduleStore.locationCalendars.length === 0
    ) {
      if(nextState.activeTab === 3 && SessionStore.loadingCalls.indexOf('getLocationCalendars') < 0) {
        ScheduleStore.getLocationCalendars(LocationStore.locationSid);
      }
    }
  }

  // Format location data and send it to the LocationStore
  setLocation(name, address1, address2, city, region, country, postalCode, lat, lng, preventRender) {
    const { LocationStore } = this.props.store;

    const data = {
      address1,
      address2,
      place: city,
      region,
      country,
      postalCode,
      locationLatitude: lat,
      locationLongitude: lng,
      preventRender
    }

    // If there is no location name set and there's a name returned from google, set name to data object for location update
    if (!LocationStore.locationData.name && name) {
      data.name = name
    }

    this.props.store.LocationStore.updateLocationData(data);
  }

  // Handle price model switching (i.e. flat or tiered)
  handlePriceOpts(value, index) {
    let priceGroups = [...this.state.priceGroups]
    let updatedPriceGroup = {...priceGroups[index]}

    updatedPriceGroup.priceModel = value;

    priceGroups.splice(index, 1, updatedPriceGroup);

    // If setting pricing model to flat, remove the extra fee object
    if(value === 'flat') {
      updatedPriceGroup.fees.length = 1;
      updatedPriceGroup.fees[0].maximumSessionLength = 480;
      updatedPriceGroup.sessionLength = [0, 480];
    }

    this.setState({
      priceGroups
    });
  }

  // Update late lead time
  updatePriceGroupData(i) {
    const { LocationStore } = this.props.store;

    let data = parseInt(this[`leadTime${i}`].value, 10) || 0;

    LocationStore.updateAvailabilityData(i, data, 'lateSchedulingLeadTime');

    // Update local state
    let priceGroups = [...this.state.priceGroups]
    let updatedPriceGroup = {...priceGroups[i]}
    updatedPriceGroup.lateSchedulingLeadTime = parseInt(this[`leadTime${i}`].value, 10) || 0;

    if(data === 0) {
      updatedPriceGroup.fees.forEach(function(element) {
        Object.keys(element.amounts).map(
          (key) =>
          {
            element.amounts[key].late = 0;
            return true;
          }
        )
      });
    }

    priceGroups.splice(i, 1, updatedPriceGroup);
    this.setState({
      priceGroups
    });
  }

  // Update calendar
  updateCalendar(e, i) {
    const { LocationStore } = this.props.store;

    let data = e.target.value;

    LocationStore.updateAvailabilityData(i, data, 'smarterSchedulingCalendarSid');

    let priceGroups = [...this.state.priceGroups];
    let updatedPriceGroup = {...priceGroups[i]};
    updatedPriceGroup.smarterSchedulingCalendarSid = data;

    priceGroups.splice(i, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  // Update exam group
  updateExamGroup(e, i, name) {
    const { LocationStore } = this.props.store;

    let data = e.target.value;
    let prop = 'registerBlastExamGroup';

    if(name === 'written') {
      prop = 'registerBlastWrittenExamGroupKey';
    }

    if(name === 'hybrid') {
      prop = 'registerBlastHybridExamGroupKey';
    }

    LocationStore.updateAvailabilityData(i, data, prop);

    let priceGroups = [...this.state.priceGroups];
    let updatedPriceGroup = {...priceGroups[i]};
    updatedPriceGroup[prop] = data;

    priceGroups.splice(i, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  // Set default timezone and currency
  updateLocationOpts() {
    let timeZoneSid = this.timezone.value || null;
    let currencyCode = this.defaultCurrency.value || null;
    let proctorLocationTypeSid = this.locationType.value || null;
    let priceGroups = [...this.state.priceGroups]
    let updatedPriceGroup = {...priceGroups[0]};

    const data = {
      timeZoneSid,
      currencyCode,
      proctorLocationTypeSid,
      preventRender: true
    }

    let fees = {
      minimumSessionLength: 0,
      maximumSessionLength: 480,
      amounts: {
        [currencyCode]: {
          service: 0,
          late: 0
        }
      }
    }

    updatedPriceGroup.fees = [fees];
    updatedPriceGroup.currencies = [currencyCode];
    updatedPriceGroup.sessionLength = [0, 480];

    priceGroups.splice(0, 1, updatedPriceGroup);

    this.setState({
      currencyCode,
      timeZoneSid,
      proctorLocationTypeSid,
      priceGroups
    });

    this.props.store.LocationStore.updateLocationData(data);
  }

  // Add or remove currencies
  updateCurrencies(value, index) {
    let priceGroups = [...this.state.priceGroups]
    let updatedPriceGroup = {...priceGroups[index]}

    // Add to currencies array
    updatedPriceGroup.currencies = value;

    // Add new currencies to fees array
    let updatedFees = [];
    let updatedAmounts = {};

    for(let i = 0; i < updatedPriceGroup.fees.length; i++) {
      let newFeeObject = {};

      for(let j = 0; j < value.length; j++) {
        if(j > updatedPriceGroup.fees.length) {
          updatedAmounts[value[j]] = {
            service: 0,
            late: 0
          }
        } else {
          updatedAmounts = updatedPriceGroup.fees[i].amounts;

          // Remove old currencies
          removeOld(updatedAmounts);
        }
      }

      function removeOld(updatedAmounts) {
        Object.keys(updatedAmounts).map(
          (key) => {
            if(updatedPriceGroup.currencies.indexOf(key) === -1) {
              delete updatedAmounts[key]
            }
            return updatedAmounts;
          }
        )
      }

      newFeeObject.minimumSessionLength = updatedPriceGroup.fees[i].minimumSessionLength;
      newFeeObject.maximumSessionLength = updatedPriceGroup.fees[i].maximumSessionLength;
      newFeeObject.amounts = updatedAmounts;
      updatedFees.push(newFeeObject);
    }

    updatedPriceGroup.fees = updatedFees;

    // Update state
    priceGroups.splice(index, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  // Show or hide the currency selector
  editCurrencies(index) {
    let priceGroups = [...this.state.priceGroups]
    let updatedPriceGroup = {...priceGroups[index]}

    updatedPriceGroup.editingCurrencies = !updatedPriceGroup.editingCurrencies;

    priceGroups.splice(index, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  // Add new price group (availability)
  addPriceGroup() {
    // Make API call
    if(this.priceGroup.value) {
      this.props.store.LocationStore.createLocationAvailability(this.priceGroup.value, this);
    }

    // Reset select element
    this.priceGroup.value = '';
  }

  // Remove price group (availability)
  removePriceGroup(index, sid) {
    this.props.store.LocationStore.removeLocationAvailability(sid, this);

    const priceGroups = [...this.state.priceGroups];
    priceGroups.splice(index, 1);

    this.setState({
      priceGroups
    });
  }

  // Enable or disable price group (availability)
  togglePriceGroup(e, i) {
    const { translation } = this.props;
    const { LocationStore, SessionStore } = this.props.store;
    LocationStore.toggleLocationAvailability(i);

    // Show a warning message if user is disabling
    // the global price group
    if(!e.target.checked && !this.state.priceGroups[i].account) {
      const content = translation.disable_msg;
      SessionStore.showDialog(content);
    }
  }

  sortNumber(a, b) {
    return a - b;
  }

  sortObject(a, b) {
    if(a.minimumSessionLength < b.minimumSessionLength) {
      return -1;
    } else if(a.minimumSessionLength > b.minimumSessionLength) {
      return 1;
    }

    return 0;
  }

  // Add new range to tiered pricing
  addRange(activePriceGroup, index) {
    const rangeCount = activePriceGroup.sessionLength.length;
    const upperBound = activePriceGroup.sessionLength[rangeCount - 1];
    const lowerBound = activePriceGroup.sessionLength[rangeCount - 2];
    let newBound = (upperBound - lowerBound) / 2 + lowerBound;
    newBound = Math.ceil(newBound / 30) * 30;

    // Make sure the new range is smaller than the limit
    if(newBound < upperBound) {
      const priceGroups = [...this.state.priceGroups];
      let updatedPriceGroup = {...priceGroups[index]};

      // Add new session length
      let sessionLength = [...updatedPriceGroup.sessionLength.concat(newBound)];
      sessionLength.sort(this.sortNumber);
      updatedPriceGroup.sessionLength = sessionLength;

      // Add new fees object and modify existing
      let fees = [...updatedPriceGroup.fees];
      let lastFees = fees.pop();
      let newFees = {...lastFees};

      newFees.minimumSessionLength = lowerBound;
      newFees.maximumSessionLength = newBound;
      lastFees.minimumSessionLength = newBound;

      updatedPriceGroup.fees = updatedPriceGroup.fees.concat(newFees);
      updatedPriceGroup.fees.sort(this.sortObject);

      // Update state
      priceGroups.splice(index, 1, updatedPriceGroup);

      this.setState({
        priceGroups
      });
    }
  }

  // Remove range from tiered pricing
  removeRange(activePriceGroup, index, i) {
    const priceGroups = [...this.state.priceGroups];
    let updatedPriceGroup = {...priceGroups[index]};
    let sessionLength = [...updatedPriceGroup.sessionLength];
    let fees = [...updatedPriceGroup.fees];
    sessionLength.splice(i, 1);
    fees.splice(i - 1, 1);

    updatedPriceGroup.sessionLength = sessionLength;
    updatedPriceGroup.fees = fees;
    priceGroups.splice(index, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  // Update ranges when dragged
  onRangeChange(sessionLength, index) {
    const priceGroups = [...this.state.priceGroups];
    let updatedPriceGroup = {...priceGroups[index]};

    // Update session lengths
    updatedPriceGroup.sessionLength = sessionLength;

    // Update fees objects
    let fees = [...updatedPriceGroup.fees];

    for(let j = 0; j < fees.length; j++) {
      let updatedFees = {...fees[j]}
      let lowerBound = updatedPriceGroup.sessionLength[j];
      let upperBound = updatedPriceGroup.sessionLength[j + 1];

      updatedFees.minimumSessionLength = lowerBound;
      updatedFees.maximumSessionLength = upperBound;

      fees[j] = updatedFees;
    }

    updatedPriceGroup.fees = fees;

    // Update state
    priceGroups.splice(index, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  // Update fee properties on input
  updateFee(e, priceGroupIndex, currencyIndex, sessionIndex, fieldType, priceType) {
    const newFee = e.target.value || 0;
    const priceGroups = [...this.state.priceGroups];
    let updatedPriceGroup = {...priceGroups[priceGroupIndex]};
    let currencyName = updatedPriceGroup.currencies[currencyIndex];

    // If flat, set the session index to 0 since there is only one fee element
    if(priceType === 'flat') {
      sessionIndex = 0;
    }

    // Update fees objects
    let fees = [...updatedPriceGroup.fees];

    if(fieldType === 'service') {
      let amounts = {...fees[sessionIndex].amounts};
      let currency = {...amounts[currencyName]}
      let service = {...currency.service};

      for(let i = 0; i < fees.length; i++) {
        if(i === sessionIndex) {
          service = newFee;
          currency.service = service;
          amounts[currencyName] = currency;
          fees[i].amounts = amounts;
        }
      }
    } else {
      for(let i = 0; i < fees.length; i++) {
        let amounts = {...fees[i].amounts};
        let currency = {...amounts[currencyName]}
        let late = {...currency.late};

        late = newFee;
        currency.late = late;
        amounts[currencyName] = currency;
        fees[i].amounts = amounts;
      }
    }

    // Update state
    priceGroups.splice(priceGroupIndex, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  applyPolicy(e, priceGroupIndex) {
    const { LocationStore } = this.props.store;
    const priceGroups = [...this.state.priceGroups];
    let updatedPriceGroup = {...priceGroups[priceGroupIndex]};

    let data = e.target.value;

    if(data) {
      updatedPriceGroup.cancellationPolicySid = data;

      LocationStore.updateAvailabilityData(
        priceGroupIndex,
        data,
        'cancellationPolicySid'
      );
    } else {
      updatedPriceGroup.cancellationPolicySid = null;
    }

    // Update state
    priceGroups.splice(priceGroupIndex, 1, updatedPriceGroup);

    this.setState({
      priceGroups
    });
  }

  handleTabs(index) {
    this.setState({
      activeTab: index + 1,
    })
  }

  // Decide what to do when tab form is submitted
  handleContinue(e) {
    toTop(document.querySelector('.drawer.open'));

    // If it’s the last tab
    if(this.state.activeTab === this.state.tabs.length) {
      // If we’re registering, go to next step
      if(this.props.routing.location.pathname.indexOf('register') > -1) {
        this.props.nextStep(e);
      }
      // Otherwise, close the drawer
      else if(this.props.checkup) {
        closeDrawer(
          null,
          this.props.parent
        );

        // Mark the location as confirmed
        const confirmedLocations = [...this.props.parent.state.confirmedLocations];

        confirmedLocations.push(this.props.locationSid);

        this.props.parent.setState({
          confirmedLocations
        });
      } else {
        closeDrawer(
          null,
          this.props.parent,
          '/locations'
        );

        this.props.store.SessionStore.showToaster(this.props.translation.success, 'success');

        this.props.store.LocationStore.getLocations();

        document.querySelector('body').classList.remove('drawer-open');
        document.querySelector('body').classList.remove('drawer-open');

      }
    } else {
      document.querySelector('form.location-form').className = document.querySelector('form.location-form').className.replace('submitted', '');

      // Click the next tab if we’re not already on the last one
      let tabs = document.querySelectorAll('.ReactTabs__Tab');
      let nextTab = tabs[this.state.activeTab];
      nextTab.click();
    }
  }

  // Decide what to do when the back button is clicked
  handleBack(e) {
    this.props.store.LocationStore.renderMap = true;

    // If we’re registering and we’re already on the first tab,
    // go back to the previous step
    if(this.state.activeTab === 1) {
      this.props.prevStep(e);
    } else {
      // Otherwise go back to the previous tab
      document.querySelector('form.location-form').className = document.querySelector('form.location-form').className.replace('submitted', '');

      let tabs = document.querySelectorAll('.ReactTabs__Tab');
      let prevTab = tabs[this.state.activeTab - 2];
      prevTab.click();
    }
  }

  // Update boolean location data (smoke free, technology available, etc.)
  handleToggle(key) {
    const { LocationStore } = this.props.store;
    LocationStore.locationData[key] = !LocationStore.locationData[key];
    this.handleInput();
  }

  // Handle location text input
  handleInput() {
    let data = null;

    if(this.state.activeTab === 1) {
      // We only submit the name and address on the first tab
      data = {
        name: this.name.value || null,
        address1: this.address1.value || null,
        address2: this.address2.value || null,
        place: this.place.value || null,
        region: this.region.value || null,
        postalCode: this.postalCode.value || null,
        country: this.country.value || null
      }
    } else {
      // Second tab contains more detailed location data
      data = {
        notes: this.notes.value || null,
        smokeFree: this.smokeFree.checked,
        parkingInformation: this.parkingInformation.value || null,
        hasComputerAvailable: this.hasComputerAvailable.checked,
        hasPrinterAvailable: this.hasPrinterAvailable.checked,
        hasCopierAvailable: this.hasCopierAvailable.checked,
        hasFaxAvailable: this.hasFaxAvailable.checked,
        hasElevator: this.hasElevator.checked,
        hasWheelchairRamp: this.hasWheelchairRamp.checked,
        hasAlternativeKeyboards: this.hasAlternativeKeyboards.checked,
        hasScreenEnlarger: this.hasScreenEnlarger.checked,
        hasScreenReader: this.hasScreenReader.checked,
        hasSpeechRecognition: this.hasSpeechRecognition.checked,
        hasTextToSpeech: this.hasTextToSpeech.checked,
        hasDistractionFreeRooms: this.hasDistractionFreeRooms.checked,
        timeZoneSid: this.timezone.value || null,
        currencyCode: this.defaultCurrency.value || null,
        proctorLocationTypeSid: this.locationType.value || null
      }
    }

    // Don’t re-render
    data.preventRender = true;

    this.props.store.LocationStore.updateLocationData(data);
  }

  // Decide what to do when a location form is submitted
  handleLocationSubmit(e) {
    if(this.state.create) {
      // If this is the first submission for the location (i.e. first tab of add form)
      // create it and set the state create to false so it doesn’t get created again
      this.createLocation(e);
      this.setState({
        create: false
      });

    } else {
      // Otherwise, update it
      this.updateLocation(e);
    }

    if(document.querySelector('.drawer')) {
      document.querySelector('.drawer').scrollTop = 0;
    }

  }

  createLocation(e) {
    this.props.store.LocationStore.createLocation(this.handleContinue, this.props.parent);
  }

  updateLocation(e) {
    this.props.store.LocationStore.updateLocation(this.handleContinue, this.props.locationSid);
  }

  createLocationAvailability() {
    this.props.store.LocationStore.createLocationAvailability();
  }

  handleFeesSubmit(e) {
    this.props.store.LocationStore.updateFees(this.state.priceGroups, this.handleContinue);

  }

  renderPolicies(i) {
    const { translation } = this.props;

    return (
      <fieldset className="form-section">
        <label htmlFor={`cancellation-${i}`}>
          {translation.cancellation_policy}
        </label>

        <label className="select">
          <select
            id={`cancellation-${i}`}
            onChange={(e) => this.applyPolicy(e, i)}
            defaultValue={this.state.priceGroups[i].cancellationPolicySid}
          >
            <option value="">{translation.none_selected}</option>
            {this.props.store.PolicyStore.cancellationPolicies.map(
              (policy) =>
              <option key={policy.sid} value={policy.sid}>{policy.title}</option>
            )}
          </select>
        </label>
      </fieldset>
    )
  }

  // Main render
  render() {
    const { translation } = this.props;
    const { LocationStore, SessionStore } = this.props.store;
    const { locationData } = LocationStore;

    const center = {
      lat: locationData.locationLatitude || 37.09024,
      lng: locationData.locationLongitude || -95.712891
    }

    let tabClass = '';

    // Disable clicking of tabs if we’re creating a new location
    if(this.state.newLocation || this.props.checkup) {
      tabClass = 'tabs-disabled';
    } else {
      tabClass = 'tabs-enabled';
    }

    // Don’t use the default tab styles
    Tabs.setUseDefaultStyles(false);

    // Default map options
    let zoom = 4;
    let dropPin = false;
    let locationClass = null;

    if(locationData.locationLatitude) {
      // If it’s not a new location, increase the zoom and drop a pin
      zoom = 17;
      dropPin = true;
      locationClass = 'location--selected';
    } else {
      // Otherwise, just render the map with the default options
      LocationStore.renderMap = true;
    }

    // Default select values
    const _this = this;
    function defaultValue(value) {
      if(_this.state.newLocation) {
        return '';
      } else {
        return value;
      }
    }

    return(
      <div className="panel">
        <div className="panel__header">
          {/* New location heading */}
          {this.state.newLocation &&
            <h3 className="panel__title"><legend>{translation.add_heading}</legend></h3>
          }

          {/* Edit location heading */}
          {!this.state.newLocation &&
            <h3 className="panel__title"><legend>{translation.edit_heading}</legend></h3>
          }
        </div>

        <div className="panel__body">
          <article className={`location ${locationClass}`}>
            <Tabs
              onSelect={this.handleTabs}
            >
              <TabList className={tabClass}>
                <Tab>{translation.location}</Tab>
                <Tab>{translation.details}</Tab>
                <Tab>{translation.pricing}</Tab>
              </TabList>

              <TabPanel className="location__search">
                <h4 className="meta">{translation.location}</h4>

                <span className="note note--prominent"><icons.iconExclamation /> {translation.disclaimer}</span>

                <form className="location-form" onSubmit={(e) => {e.preventDefault()}} noValidate>
                  <Map
                    setLocation={this.setLocation}
                    store={this.props.store}
                    center={center}
                    zoom={zoom}
                    dropPin={dropPin}
                    locationSid={this.props.locationSid}
                  />

                  <fieldset className="location__address">
                    <ul className="fields">
                      <li>
                        <label
                          className="lbl-required"
                          htmlFor="map-name"
                        >
                          {translation.location_name}
                        </label>

                        <input
                          type="text"
                          id="map-name"
                          value={locationData.name || ''}
                          onChange={this.handleInput}
                          ref={(input) => this.name = input}
                          required
                          aria-required="true"
                          disabled={locationData.locked}
                        />
                      </li>
                      <li>
                        <label
                          className="lbl-required"
                          htmlFor="map-addr1"
                        >
                          {translation.address}
                        </label>

                        <input
                          type="text"
                          id="map-addr1"
                          value={locationData.address1 || ''}
                          placeholder={translation.street_address}
                          onChange={this.handleInput}
                          ref={(input) => this.address1 = input}
                          required
                          aria-required="true"
                          disabled={locationData.locked}
                        />

                        <label
                          className="meta"
                          htmlFor="map-addr2"
                        >
                          {translation.address_2}
                        </label>

                        <input
                          type="text"
                          id="map-addr2"
                          value={locationData.address2 || ''}
                          placeholder={translation.suite}
                          onChange={this.handleInput}
                          ref={(input) => this.address2 = input}
                          disabled={locationData.locked}
                        />
                      </li>
                      <li>
                        <label
                          className="lbl-required"
                          htmlFor="map-city"
                        >
                          {translation.city}
                        </label>

                        <input
                          type="text"
                          id="map-city"
                          value={locationData.place || ''}
                          onChange={this.handleInput}
                          ref={(input) => this.place = input}
                          disabled={locationData.locked}
                        />
                      </li>
                      <li className="field--third">
                        <label
                          className="lbl-required"
                          htmlFor="map-region"
                        >
                          {translation.region}
                        </label>

                        <input
                          type="text"
                          id="map-city"
                          value={locationData.region || ''}
                          onChange={this.handleInput}
                          ref={(input) => this.region = input}
                          disabled={locationData.locked}
                        />
                      </li>
                      <li className="field--third">
                        <label
                          className="lbl-required"
                          htmlFor="map-postal"
                        >
                          {translation.postal}
                        </label>

                        <input
                          type="text"
                          id="map-city"
                          value={locationData.postalCode || ''}
                          onChange={this.handleInput}
                          ref={(input) => this.postalCode = input}
                          disabled={locationData.locked}
                        />
                      </li>
                      <li className="field--third">
                        <label
                          className="lbl-required"
                          htmlFor="map-country"
                        >
                          {translation.country}
                        </label>

                        <input
                          type="text"
                          id="map-city"
                          value={locationData.postalCode || ''}
                          onChange={this.handleInput}
                          ref={(input) => this.country = input}
                          disabled={locationData.locked}
                        />
                      </li>
                    </ul>
                  </fieldset>
                </form>
              </TabPanel>

              <TabPanel>
                <h4 className="meta">{translation.details}</h4>

                <form className="location-form" onSubmit={(e) => checkValidity(e, this.handleLocationSubmit, 'form.location-form')} noValidate>
                  <fieldset>
                    <ul className="fields">
                      <li>
                        <label className="lbl-required" htmlFor="location-timezone">{translation.timezone}</label>
                        <label className="select">
                          <select
                            id="location-timezone"
                            defaultValue={
                              getURLSegment(this, 1) === 'checkup' ?
                              '' :
                              defaultValue(locationData.timeZoneSid)
                            }
                            onChange={this.updateLocationOpts}
                            ref={(input) => {this.timezone = input}}
                            required
                            aria-required="true"
                            disabled={locationData.locked}
                          >
                            <option value="">{translation.timezone_helper}</option>
                            {SessionStore.timezoneList.map(
                              (key, i) =>
                              <option key={i} value={key.sid}>{key.name.replace('_', ' ')}</option>
                            )}
                          </select>
                        </label>
                      </li>
                      <li>
                        <label className="lbl-required" htmlFor="location-default-currency">{translation.default_currency}</label>
                        <label className="select">
                          <select
                            id="location-default-currency"
                            defaultValue={defaultValue(locationData.currencyCode)}
                            onChange={this.updateLocationOpts}
                            ref={(input) => {this.defaultCurrency = input}}
                            required
                            aria-required="true"
                            disabled={locationData.locked}
                          >
                            <option value="">{translation.currency_helper}</option>
                            {SessionStore.currencyList.map(
                              (key, i) =>
                              <option key={i} value={key.code}>{key.name}</option>
                            )}
                          </select>
                        </label>
                      </li>
                      <li>
                        <label
                          className="lbl-required"
                          htmlFor="location-type"
                        >
                          {translation.location_type.heading}
                        </label>

                        <label className="select">
                          <select
                            id="location-type"
                            defaultValue={
                              getURLSegment(this, 1) === 'checkup' ?
                              '' :
                              defaultValue(locationData.proctorLocationTypeSid)
                            }
                            onChange={this.updateLocationOpts}
                            ref={(input) => {this.locationType = input}}
                            required
                            aria-required="true"
                            disabled={locationData.locked}
                          >
                            <option value="">{translation.location_type_helper}</option>
                            {LocationStore.locationTypesList.map(
                              (key, i) => {
                                if(
                                  key.sid !== config.coffeeShopSid
                                ) {
                                  return (
                                    <option key={i} value={key.sid}>{key.title}</option>
                                  )
                                }

                                return null;
                              }
                            )}
                          </select>
                        </label>
                      </li>
                      <li>
                        <label className="lbl-optional" htmlFor="location-notes">
                          {translation.notes.label}
                        </label>

                        <Textarea
                          id="location-notes"
                          className="textarea-grow"
                          onChange={this.handleInput}
                          ref={(input) => {this.notes = input}}
                          defaultValue={locationData.notes}
                        ></Textarea>
                      </li>
                      <li>
                        <label htmlFor="location-smoke-free">{translation.smoke_free}</label>
                        <input
                          type="checkbox"
                          id="location-smoke-free"
                          className="switch-input"
                          checked={locationData.smokeFree}
                          onChange={() => {this.handleToggle('smokeFree')}}
                          ref={(input) => {this.smokeFree = input}}
                          disabled={locationData.locked}
                        />
                        <label htmlFor="location-smoke-free" className="switch-label"><span className="meta">{translation.smoke_free}</span></label>
                      </li>
                      <li>
                        <label className="lbl-optional" htmlFor="location-parking">{translation.parking_instructions.label}</label>
                        <Textarea
                          id="location-parking"
                          className="textarea-grow"
                          onChange={this.handleInput}
                          ref={(input) => {this.parkingInformation = input}}
                          defaultValue={locationData.parkingInformation}
                          disabled={locationData.locked}
                        ></Textarea>
                      </li>
                    </ul>

                    <h4 className="form-heading">{translation.technology.heading}</h4>

                    <ul className="switch-list">
                      {
                        Object
                          .keys(translation.technology.options)
                          .map(
                            (key) =>
                            <li key={key}>
                              <label htmlFor={`location-tech-${key}`}>
                                {translation.technology.options[key].label}
                              </label>

                              <input
                                type="checkbox"
                                id={`location-tech-${key}`}
                                name="tech[]"
                                className="switch-input"
                                checked={locationData[key]}
                                onChange={() => {this.handleToggle(key)}}
                                ref={(input) => {this[key] = input}}
                                disabled={locationData.locked}
                              />

                              <label className="switch-label" htmlFor={`location-tech-${key}`}>
                                <span className="meta">
                                  {translation.technology.options[key].label}
                                </span>
                              </label>
                            </li>
                          )
                      }
                    </ul>

                    <h4 className="form-heading">{translation.accessibility.heading}</h4>

                    <ul className="switch-list">
                      {
                        Object
                          .keys(translation.accessibility.options)
                          .map(
                            (key) =>
                            <li key={key}>
                              <label htmlFor={`location-accessibility-${key}`}>
                                {translation.accessibility.options[key].label}
                              </label>

                              <input
                                type="checkbox"
                                id={`location-accessibility-${key}`}
                                name="accessibility[]"
                                className="switch-input"
                                checked={locationData[key]}
                                onChange={() => {this.handleToggle(key)}}
                                ref={(input) => {this[key] = input}}
                                disabled={locationData.locked}
                              />

                              <label className="switch-label" htmlFor={`location-accessibility-${key}`}>
                                <span className="meta">
                                  {translation.accessibility.options[key].label}
                                </span>
                              </label>
                            </li>
                          )
                      }
                    </ul>
                  </fieldset>
                </form>
              </TabPanel>

              <TabPanel>
                <h4 className="meta">{translation.pricing}</h4>

                <form className="location-form" onSubmit={(e) => checkValidity(e, this.handleContinue, 'form.location-form')} noValidate>
                  <fieldset>
                    {this.renderPriceGroup()}
                  </fieldset>
                </form>

                <label className="select">
                  <span className="meta">{translation.institution_pricing}</span>

                  <select ref={(input) => {this.priceGroup = input}}>
                    <option value="">{translation.institution_pricing}</option>
                    {SessionStore.accountList.map(
                      (key, i) =>
                      <option key={i} value={key.sid}>{key.name}</option>
                    )}
                  </select>
                </label>

                <button className="btn btn--outline" onClick={this.addPriceGroup} type="button">{translation.add_institution}</button>
              </TabPanel>
            </Tabs>
          </article>

          <div>
            {/* New location buttons */}
            {this.state.newLocation &&
              <div className="panel__actions actions">
                {(this.state.activeTab !== 1 || this.props.routing.location.pathname.indexOf('register') > -1) &&
                  <button className="actions__btn actions__btn--secondary" onClick={(e) => this.handleBack(e)} type="button">{translation.back}</button>
                }

                {this.state.activeTab !== 2 && this.state.activeTab !== 1 &&
                  <button className="actions__btn actions__btn--primary" onClick={(e) => checkValidity(e, this.handleFeesSubmit, 'form.location-form')}>{translation.button}</button>
                }

                {(this.state.activeTab === 2 || this.state.activeTab === 1) &&
                  <button className="actions__btn actions__btn--primary" onClick={(e) => checkValidity(e, this.handleLocationSubmit, 'form.location-form')}>{translation.button}</button>
                }
              </div>
            }

            {/* Edit location buttons */}
            {!this.state.newLocation &&
              <div className="panel__actions actions">
                {/* If we’re not on tab 1 or 2 we’re dealing with fees */}
                {this.state.activeTab !== 2 && this.state.activeTab !== 1 &&
                  <button
                    className="actions__btn actions__btn--primary"
                    onClick={(e) => checkValidity(e, this.handleFeesSubmit, 'form.location-form')}
                  >
                    {this.props.checkup &&
                      translation.button
                    }

                    {!this.props.checkup &&
                      translation.save
                    }
                  </button>
                }

                {/* If we’re on tab 1 or 2, we’re dealing with locations */}
                {(this.state.activeTab === 2 || this.state.activeTab === 1) &&
                  <button
                    className="actions__btn actions__btn--primary"
                    onClick={(e) => checkValidity(e, this.handleLocationSubmit, 'form.location-form')}
                  >
                    {this.props.checkup &&
                      translation.button
                    }

                    {!this.props.checkup &&
                      translation.save
                    }
                  </button>
                }
              </div>
            }
          </div>
        </div>
      </div>
    )
  }

  // Render price groups (i.e. availabilities)
  renderPriceGroup() {
    const { LocationStore, ScheduleStore, SessionStore } = this.props.store;
    const { translation } = this.props;

    if(LocationStore.locationAvailability.length === 0) {
      return null;
    } else {
      return(
        <div className="fields">
          {this.state.priceGroups.map(
            (key, i) =>
            <div
              className={`
                price-group
                ${!LocationStore.locationAvailability[i].enabled ? 'disabled' : ''}
              `}
              key={i}
            >
              <h4 className="form-heading">
                {this.state.priceGroups[i].account &&
                  <span>
                    {/* Account name heading for non-default */}
                    {this.state.priceGroups[i].account.sid &&
                      `${this.state.priceGroups[i].account.name} ${translation.pricing}`
                    }

                    {/* Show remove button for non-default */}
                    {this.state.priceGroups[i].account.sid &&
                      <button className="btn--link alt-link" onClick={() => this.removePriceGroup(i, this.state.priceGroups[i].sid)} type="button">{translation.remove}</button>
                    }
                  </span>
                }

                {/* Generic Global Pricing heading for default */}
                {!this.state.priceGroups[i].account &&
                  <span>{translation.global} {translation.pricing}</span>
                }
              </h4>

              <div className="price-group__enabled">
                <label
                  htmlFor={`g${i}-enabled`}
                  className="switch-label--alt"
                >
                  {
                    LocationStore.locationAvailability[i].enabled ?
                    translation.enabled :
                    translation.disabled
                  }
                </label>

                <input
                  type="checkbox"
                  id={`g${i}-enabled`}
                  className="switch-input"
                  checked={LocationStore.locationAvailability[i].enabled}
                  onChange={(e) => this.togglePriceGroup(e, i)}
                />

                <label
                  htmlFor={`g${i}-enabled`}
                  className="switch-label"
                >
                  <span className="meta">
                    {
                      LocationStore.locationAvailability[i].enabled ?
                      translation.enabled :
                      translation.disabled
                    }
                  </span>
                </label>
              </div>

              {LocationStore.locationAvailability[i].enabled &&
                <div>
                  <ul className="fields">
                    <li>
                      <label className="lbl-optional" htmlFor={`g${i}-lead-time`} aria-describedby="leadtime-tooltip">
                        {translation.lead_time.label}
                        <div className="tooltip">
                          <span className="tooltip__label">(?)</span>
                          <div id="leadtime-tooltip" className="tooltip__body" role="tooltip">
                            {/* Lead time selected message */}
                            {this.state.priceGroups[i].lateSchedulingLeadTime !== 0 &&
                              <span>{translation.lead_time.late_fee_start} {this.state.priceGroups[i].lateSchedulingLeadTime} {translation.lead_time.hours} {translation.lead_time.late_fee_end}</span>
                            }

                            {/* No lead time selected message */}
                            {this.state.priceGroups[i].lateSchedulingLeadTime === 0 &&
                              <span>{translation.lead_time.no_late_fee}</span>
                            }
                          </div>
                        </div>
                      </label>

                      <label className="select" htmlFor={`g${i}-lead-time`}>
                        <select id={`g${i}-lead-time`} defaultValue={this.state.priceGroups[i].lateSchedulingLeadTime} onChange={() => this.updatePriceGroupData(i)} ref={(input) => {this[`leadTime${i}`] = input}}>
                          <option value={0}>0 {translation.lead_time.hours}</option>
                          <option value={6}>6 {translation.lead_time.hours}</option>
                          <option value={12}>12 {translation.lead_time.hours}</option>
                          <option value={18}>18 {translation.lead_time.hours}</option>
                          <option value={24}>24 {translation.lead_time.hours}</option>
                          <option value={30}>30 {translation.lead_time.hours}</option>
                          <option value={36}>36 {translation.lead_time.hours}</option>
                          <option value={42}>42 {translation.lead_time.hours}</option>
                          <option value={48}>48 {translation.lead_time.hours}</option>
                          <option value={60}>60 {translation.lead_time.hours}</option>
                          <option value={72}>72 {translation.lead_time.hours}</option>
                        </select>
                      </label>
                    </li>
                    <li>
                      <label className="lbl-required">{translation.price_model}</label>

                      <RadioGroup
                        name={`price-model${i}`}
                        selectedValue={this.state.priceGroups[i].priceModel}
                        onChange={(value) => this.handlePriceOpts(value, i)}
                      >
                        <Radio
                          id={`g${i}-pricing-tiered`}
                          value="tiered"
                        />

                        <label
                          htmlFor={`g${i}-pricing-tiered`}
                          className="lbl-inline"
                        >
                          {translation.tiered}
                        </label>

                        <Radio
                          id={`g${i}-pricing-flat`}
                          value="flat"
                        />

                        <label
                          htmlFor={`g${i}-pricing-flat`}
                          className="lbl-inline"
                        >
                          {translation.flat_rate}
                        </label>
                      </RadioGroup>
                    </li>
                  </ul>

                  {this.renderPricing(this.state.priceGroups[i], i)}

                  <CheckboxGroup
                    className="location__currencies form-section"
                    name="currencies"
                    value={this.state.priceGroups[i].currencies}
                    onChange={(value) => this.updateCurrencies(value, i)}
                  >
                    <button
                      className="btn--link"
                      onClick={() => this.editCurrencies(i)}
                      type="button"
                    >
                      {!this.state.priceGroups[i].editingCurrencies &&
                        <span>{translation.edit_currencies}</span>
                      }
                      {this.state.priceGroups[i].editingCurrencies &&
                        <span>{translation.done_editing}</span>
                      }
                    </button>

                    {this.state.priceGroups[i].editingCurrencies &&
                      <ul id="location-currencies" className="checklist">
                        {SessionStore.currencyList.map(
                          (key, j) =>
                          <li key={j}>
                            <Checkbox
                              id={`g${i}c${j}`}
                              className="switch-input"
                              value={key.code}
                            />

                            <label
                              htmlFor={`g${i}c${j}`}
                              className="switch-label"
                            >
                              <span className="meta">{key.name}</span>
                            </label>

                            <label
                              htmlFor={`g${i}c${j}`}
                              className="switch-label--alt"
                            >
                              {key.name}
                            </label>
                          </li>
                        )}
                      </ul>
                    }
                  </CheckboxGroup>

                  <FeatureFlag
                    flagKey="proctor-cancellation-policies"
                    renderFeatureCallback={() => this.renderPolicies(i)}
                  />

                  {ScheduleStore.locationCalendars.length > 0 &&
                    <div className="location__exam-group">
                      <h5>{translation.calendar.heading}</h5>

                      <label className="select">
                        <span className="meta">{translation.calendar.label}</span>

                        <select
                          defaultValue={this.state.priceGroups[i].smarterSchedulingCalendarSid}
                          onChange={(e) => this.updateCalendar(e, i)}
                          ref={(input) => {this[`calendar${i}`] = input}}
                        >
                          <option value="">{translation.calendar.label}</option>
                          {ScheduleStore.locationCalendars.map(
                            (key, i) =>
                            <option key={i} value={key.sid}>{key.title}</option>
                          )}
                        </select>
                      </label>
                    </div>
                  }

                  {LocationStore.examGroups.length > 0 &&
                    <div className="location__exam-group">
                      <h5>
                        <img className="exam-group-logo" src={rbIcon} alt="RegisterBlast" height="30" />

                        {translation.exam_group.heading}
                      </h5>

                      <ul className="field form-inline">
                        <li>
                          <label htmlFor="rb-online-exam-group">
                            {translation.exam_group.label_online}
                          </label>

                          <div className="select">
                            <select
                              defaultValue={this.state.priceGroups[i].registerBlastExamGroup}
                              id="rb-online-exam-group"
                              onChange={(e) => this.updateExamGroup(e, i, 'online')}
                              ref={(input) => {this[`examGroup${i}`] = input}}
                            >
                              <option value="">{translation.exam_group.helper}</option>
                              {LocationStore.examGroups.map(
                                (key, i) =>
                                <option key={i} value={key.id}>{key.name}</option>
                              )}
                            </select>
                          </div>
                        </li>
                        <li>
                          <label htmlFor="rb-written-exam-group">
                            {translation.exam_group.label_written}
                          </label>

                          <div className="select">
                            <select
                              defaultValue={
                                this.state.priceGroups[i].registerBlastWrittenExamGroupKey ||
                                this.state.priceGroups[i].registerBlastExamGroup
                              }
                              id="rb-written-exam-group"
                              onChange={(e) => this.updateExamGroup(e, i, 'written')}
                              ref={(input) => {this[`writtenExamGroup${i}`] = input}}
                            >
                              <option value="">{translation.exam_group.helper}</option>
                              {LocationStore.examGroups.map(
                                (key, i) =>
                                <option key={i} value={key.id}>{key.name}</option>
                              )}
                            </select>
                          </div>
                        </li>
                        <li>
                          <label htmlFor="rb-hybrid-exam-group">
                            {translation.exam_group.label_hybrid}
                          </label>

                          <div className="select">
                            <select
                              defaultValue={
                                this.state.priceGroups[i].registerBlastHybridExamGroupKey
                              }
                              id="rb-hybrid-exam-group"
                              onChange={(e) => this.updateExamGroup(e, i, 'hybrid')}
                              ref={(input) => {this[`hybridExamGroup${i}`] = input}}
                            >
                              <option value="">{translation.exam_group.helper}</option>
                              {LocationStore.examGroups.map(
                                (key, i) =>
                                <option key={i} value={key.id}>{key.name}</option>
                              )}
                            </select>
                          </div>
                        </li>
                      </ul>
                    </div>
                  }
                </div>
              }
            </div>
          )}
        </div>
      )
    }
  }

  // Render pricing fields
  renderPricing(activePriceGroup, index) {
    let sessionLoop = [...activePriceGroup.sessionLength];
    sessionLoop.splice(0, 1);

    const { translation } = this.props;

    if(activePriceGroup.priceModel === 'tiered') {
      // Render tiered pricing fields
      return(
        <div className="pricing--tiered">
          <strong className="label-faux">{translation.prices}</strong>

          <ul className="pricing--tiered range">
            <li className="range__slider">
              <span>{translation.session_length}</span>
              <Range min={0} max={480} step={5} dots pushable tipFormatter={value => convertMinutesToTimeString(value)} value={activePriceGroup.sessionLength} onChange={(sessionLength) => this.onRangeChange(sessionLength, index)} />
              <button className="btn--add" onClick={() => this.addRange(activePriceGroup, index)} type="button"><icons.iconPlus /> {translation.add_range}</button>
            </li>
            <li className="range__data">
              <div className="table-scrollable table-scrollable--alt">
                <table className="price-table">
                  <thead>
                    <tr>
                      <td></td>
                      {sessionLoop.map(
                        (key, i) => this.renderTHead(
                          key,
                          i,
                          sessionLoop,
                          activePriceGroup,
                          index
                        )
                      )}
                      {this.state.priceGroups[index].lateSchedulingLeadTime > 0 &&
                        <th className="price-table__last">{translation.late_fee}</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {activePriceGroup.currencies.map(
                      (key, i) =>
                      <tr key={i}>
                        <th scope="row">{key}</th>
                        {sessionLoop.map(
                          (key, j) =>
                          <td key={j} data-col={`${convertMinutesToTimeString(activePriceGroup.sessionLength[i], true, true)}–${convertMinutesToTimeString(key, false, true)}`}>
                            {this.state.priceGroups[index].fees[j].amounts[activePriceGroup.currencies[i]] &&
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                pattern="[0-9]+([\.][0-9]+)?"
                                onChange={(e) => this.updateFee(e, index, i, j, 'service', 'tiered')}
                                required
                                aria-required="true"
                                value={
                                  this.state.priceGroups[index].fees[j].amounts[activePriceGroup.currencies[i]].service ?
                                  this.state.priceGroups[index].fees[j].amounts[activePriceGroup.currencies[i]].service :
                                  ''
                                }
                              />
                            }

                            {!this.state.priceGroups[index].fees[j].amounts[activePriceGroup.currencies[i]] &&
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                pattern="[0-9]+([\.][0-9]+)?"
                                onInput={(e) => this.updateFee(e, index, i, j, 'service', 'tiered')}
                                required
                                aria-required="true"
                              />
                            }
                        </td>
                        )}
                        {this.state.priceGroups[index].lateSchedulingLeadTime > 0 &&
                          <td className="price-table__last" data-col="Late Fee">
                            {this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]] &&
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                pattern="[0-9]+([\.][0-9]+)?"
                                onChange={(e) => this.updateFee(e, index, i, null, 'late', 'tiered')}
                                value={
                                  this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]].late ?
                                  this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]].late :
                                  ''
                                }
                              />
                            }

                            {!this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]] &&
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                pattern="[0-9]+([\.][0-9]+)?"
                                onInput={(e) => this.updateFee(e, index, i, null, 'late', 'tiered')}
                              />
                            }
                          </td>
                        }
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </li>
          </ul>
        </div>
      )
    } else {
      // Render flat-rate pricing fields
      return (
        <div className="pricing--flat">
          <strong className="label-faux">{translation.price}</strong>

          <ul className="fields">
            {activePriceGroup.currencies.map(
              (key, i) =>
              <li key={i}>
                <label className="lbl-required label-inline" htmlFor={`g${index}-${key}-price`}>{key} {translation.price}</label>

                {this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]] &&
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    pattern="[0-9]+([\.][0-9]+)?"
                    id={`g${index}-${key}-price`}
                    onChange={(e) => this.updateFee(e, index, i, null, 'service', 'flat')}
                    required
                    aria-required="true"
                    value={
                      this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]].service ?
                      this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]].service :
                      '0'
                    }
                  />
                }

                {!this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]] &&
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    pattern="[0-9]+([\.][0-9]+)?"
                    id={`g${index}-${key}-price`}
                    onInput={(e) => this.updateFee(e, index, i, null, 'service', 'flat')}
                    required
                    aria-required="true"
                  />
                }

                {this.state.priceGroups[index].lateSchedulingLeadTime > 0 &&
                  <span>
                    <label className="lbl-required label-inline late" htmlFor={`g${index}-${key}-late`}>{translation.late_fee}</label>

                    {this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]] &&
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        pattern="[0-9]+([\.][0-9]+)?"
                        id={`g${index}-${key}-late`}
                        onChange={(e) => this.updateFee(e, index, i, null, 'late', 'flat')}
                        value={
                          this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]].late ?
                          this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]].late :
                          '0'
                        }
                      />
                    }

                    {!this.state.priceGroups[index].fees[0].amounts[activePriceGroup.currencies[i]] &&
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        pattern="[0-9]+([\.][0-9]+)?"
                        id={`g${index}-${key}-late`}
                        onInput={(e) => this.updateFee(e, index, i, null, 'late', 'flat')}
                      />
                    }
                  </span>
                }
              </li>
            )}
          </ul>
        </div>
      )
    }
  }

  // Render pricing table
  renderTHead(key, i, sessionLoop, activePriceGroup, index) {
    console.log(activePriceGroup.sessionLength[i], key);
    let startTime = activePriceGroup.sessionLength[i];
    if(startTime !== 0){
      startTime += 1;
    }
    return(
      <th key={i} scope="col" className="lbl-required">
        
        {convertMinutesToTimeString(startTime, true)}–{convertMinutesToTimeString(key, true, false, false, true)}

        {sessionLoop.length > 1 &&
          <button className="btn--remove" onClick={() => this.removeRange(activePriceGroup, index, i+1)} type="button"><icons.iconMinus /><span className="meta">Remove Range</span></button>
        }
      </th>
    )
  }
})

export default translate('Location')(Location);



// WEBPACK FOOTER //
// ./src/app/components/Register/Location.js